import { useTranslation } from 'react-i18next'
import ReactCountryFlag from 'react-country-flag'
import { Building2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { getBusinessRegistrationLabelTranslationKey } from '@/features/organization-settings/lib/business-registration-label'
import { SUPPLIER_ORGANIZATION_NAME_MAX_LENGTH } from '@/features/suppliers/lib/supplier-organization-name'

type SupplierOrganizationIdentitySectionCardProps = {
  name: string
  localName: string
  businessRegistrationNumber: string
  country: string
  doesRequireLocalName: boolean
  nameError?: string
  variant?: 'card' | 'plain'
  onChange: (evt: React.ChangeEvent<HTMLInputElement>) => void
}

export function SupplierOrganizationIdentitySectionCard({
  name,
  localName,
  businessRegistrationNumber,
  country,
  doesRequireLocalName,
  nameError,
  variant = 'card',
  onChange,
}: SupplierOrganizationIdentitySectionCardProps) {
  const { t } = useTranslation(['installations', 'settings'])
  const countryCode = country.trim().toLowerCase()
  const businessRegLabelKey = getBusinessRegistrationLabelTranslationKey(countryCode)

  const fields = (
    <>
        {doesRequireLocalName ? (
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="localName">
                <span className="inline-flex items-center gap-1">
                  {t('installations:suppliers.edit.identity.fields.name')}
                  <ReactCountryFlag countryCode={countryCode || 'cn'} svg />
                </span>
              </Label>
              <Input id="localName" value={localName} onChange={onChange} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="name">
                <span className="inline-flex items-center gap-1">
                  {t('installations:suppliers.edit.identity.fields.name')}
                  <ReactCountryFlag countryCode="gb" svg />
                </span>
                <span className="text-destructive"> *</span>
              </Label>
              <Input
                id="name"
                value={name}
                onChange={onChange}
                maxLength={SUPPLIER_ORGANIZATION_NAME_MAX_LENGTH}
                aria-required
                aria-invalid={Boolean(nameError)}
              />
              {nameError ? <p className="text-sm text-destructive">{nameError}</p> : null}
            </div>
          </div>
        ) : (
          <div className="grid gap-2">
            <Label htmlFor="name">
              {t('installations:suppliers.edit.identity.fields.name')}
              <span className="text-destructive"> *</span>
            </Label>
            <Input
              id="name"
              value={name}
              onChange={onChange}
              maxLength={SUPPLIER_ORGANIZATION_NAME_MAX_LENGTH}
              aria-required
              aria-invalid={Boolean(nameError)}
            />
            {nameError ? <p className="text-sm text-destructive">{nameError}</p> : null}
          </div>
        )}
        <div className="grid gap-2">
          <Label htmlFor="businessRegistrationNumber">
            {t(businessRegLabelKey, { ns: 'settings' })}
          </Label>
          <Input
            id="businessRegistrationNumber"
            value={businessRegistrationNumber}
            onChange={onChange}
          />
        </div>
    </>
  )

  if (variant === 'plain') {
    return <div className="space-y-4">{fields}</div>
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Building2 className="size-5 shrink-0" />
          <CardTitle>{t('installations:suppliers.edit.identity.title')}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">{fields}</CardContent>
    </Card>
  )
}
